import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SxProps,
  TextField,
} from '@mui/material';

// specs/001-raben-country-masteradmin/mass-specs/specs/
// 20260826-country-multiselect-picker.md. Replaces the plain single-select
// `Kraj` control on all five country-filtered surfaces (Structure, Users,
// ProductsFromSuppliers, SupplierSummary, SupplierSurveys) with one shared,
// searchable, checkbox multi-select -- Goal 1/2.
//
// Deliberately i18n-agnostic (same convention as
// Superadmin/components/CountryGroupHeader): every visible/accessible
// string arrives already translated as a prop, since the five call sites
// span two different locale namespaces (superadmin.json en/pl-only, ghg.json
// all five languages) plus one call site's own keyPrefix'd `t`.
//
// Built on plain MUI `Select`/`MenuItem`/`Checkbox` (Guardrail 1: "reuse
// existing components, don't invent a new visual language") rather than
// MUI's `Autocomplete`, for two reasons: (1) Autocomplete's default
// multi-value display is chips, which is exactly the "pills" guardrail 1
// rules out; (2) every one of the five surfaces' pre-existing regression
// tests (structure.test.tsx, usersCountry.test.tsx, surveysCountry.test.tsx,
// countrySummary.test.tsx) already drive the OLD single-select `Select` via
// `getByLabelText(...)` + `getByRole('option'|'listbox', ...)` -- a `Select`
// clones every MenuItem child with `role="option"` and wraps its popup with
// `role="listbox"` for free (`@mui/material/Select/SelectInput.js`), so
// those exact queries keep resolving unchanged.
export interface CountryPickerOption {
  code: string;
  name: string;
  // Optional override for what a SELECTED option contributes to the closed
  // control's summary line. Defaults to `code` (BR-3's "codes, not display
  // names" governs the wire format the request carries -- this only
  // controls what a human reads). Structure's "Bez kraju"/"No country"
  // bucket (`code: 'none'`) is the one caller that sets this: the literal
  // string "none" is meaningful to the API, not to a person reading the
  // filter.
  summaryLabel?: string;
}

export interface CountryPickerProps {
  options: CountryPickerOption[];
  value: string[];
  onChange: (codes: string[]) => void;
  // Accessible name for the control. Used as the native `aria-label` when
  // `label` is omitted (Structure/Users -- no visible label today); when
  // `label` IS given (the three Ghg/SuperDashboard surfaces, matching their
  // current FormControl+InputLabel look) the visible label supplies the
  // accessible name instead via `aria-labelledby`, exactly like the Select
  // it replaces.
  ariaLabel: string;
  // Goal 1's explicit "all countries" empty state -- shown by the closed
  // control instead of a blank box when nothing is selected.
  allSelectedLabel: string;
  searchPlaceholder: string;
  noResultsText: string;
  // Template containing the literal substring "{{list}}", e.g.
  // "Selected: {{list}}" / "Wybrano: {{list}}" (Goal 1's summary line).
  selectedSummaryTemplate: string;
  // Optional visible floating label (renders FormControl+InputLabel, like
  // ProductsFromSuppliers/SupplierSummary/SupplierSurveys do today).
  // Omitted on Structure/Users, which have never had a visible label here.
  label?: string;
  size?: 'small' | 'medium';
  sx?: SxProps;
  id?: string;
}

let autoIdSeq = 0;

export default function CountryPicker(props: CountryPickerProps) {
  const {
    options,
    value,
    onChange,
    ariaLabel,
    allSelectedLabel,
    searchPlaceholder,
    noResultsText,
    selectedSummaryTemplate,
    label,
    size = 'small',
    sx,
    id,
  } = props;

  const [searchTerm, setSearchTerm] = React.useState('');

  // Stable id for the FormControl/InputLabel pairing when `label` is used --
  // generated once per mounted instance rather than per render.
  const generatedId = React.useRef<string>();
  if (!generatedId.current) {
    autoIdSeq += 1;
    generatedId.current = id || `country-picker-${autoIdSeq}`;
  }
  const labelId = label ? `${generatedId.current}-label` : undefined;

  // EC-4: search matches BOTH the display name and the ISO code -- "ru"
  // finds Rumunia, "RO" also finds Rumunia.
  const filteredOptions = React.useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return options;
    return options.filter(
      (option) =>
        option.name.toLowerCase().includes(query) ||
        option.code.toLowerCase().includes(query)
    );
  }, [options, searchTerm]);

  const summaryByCode = React.useMemo(() => {
    const map = new Map<string, string>();
    options.forEach((option) =>
      map.set(option.code, option.summaryLabel ?? option.code)
    );
    return map;
  }, [options]);

  // Goal 1's summary line. Reads from the FULL `options` list (via
  // `summaryByCode`), not `filteredOptions` -- an active search term must
  // never make an already-selected-but-currently-hidden country vanish from
  // this summary (search narrows what's checkable, not what's selected).
  const buildSummary = (selected: string[]) => {
    if (selected.length === 0) return allSelectedLabel;
    const list = selected
      .map((code) => summaryByCode.get(code) ?? code)
      .join(', ');
    return selectedSummaryTemplate.replace('{{list}}', list);
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const raw = event.target.value;
    onChange(typeof raw === 'string' ? raw.split(',').filter(Boolean) : raw);
  };

  const handleClose = () => {
    // Reset the search box for the next time this opens -- matches the
    // reference mockup (a fresh picker each visit), and means a stale query
    // never silently hides options next time.
    setSearchTerm('');
  };

  // Guards for the search field embedded as a Select child, below. `Select`
  // clones EVERY child (not just `MenuItem`s) with its own `onClick`
  // (toggling `child.props.value`, `undefined` here) so that clicking
  // anywhere in this row doesn't fall through to that -- and stops keys
  // typed while searching from reaching the menu's own arrow-key/typeahead
  // navigation. Escape is deliberately let through so it still closes the
  // menu while the search field has focus. Capture phase, so it runs before
  // the search TextField's own handlers and before Select's cloned ones.
  const stopSelectFromSeeingClicks = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };
  const stopSelectFromSeeingKeys = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape') event.stopPropagation();
  };

  const selectControl = (
    <Select
      multiple
      value={value}
      onChange={handleChange}
      onClose={handleClose}
      displayEmpty
      size={size}
      labelId={labelId}
      label={label}
      // Pairs with the forced-`shrink` InputLabel below: the outline's
      // legend gap must stay open too, or the border strikes through the
      // permanently-floated label.
      input={
        label ? <OutlinedInput notched label={label} size={size} /> : undefined
      }
      inputProps={{ 'aria-label': label ? undefined : ariaLabel }}
      renderValue={(selected) => (
        <Box
          component="span"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {buildSummary(selected as string[])}
        </Box>
      )}
      MenuProps={{
        autoFocus: false,
        PaperProps: { sx: { maxHeight: 380 } },
      }}
      sx={{
        '& .MuiSelect-select': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        ...(label ? {} : sx),
      }}
    >
      <ListSubheader
        onClickCapture={stopSelectFromSeeingClicks}
        onKeyDownCapture={stopSelectFromSeeingKeys}
      >
        <TextField
          autoFocus
          fullWidth
          size="small"
          variant="outlined"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          inputProps={{ 'aria-label': searchPlaceholder }}
        />
      </ListSubheader>
      {filteredOptions.map((option) => (
        <MenuItem key={option.code} value={option.code}>
          <Checkbox
            checked={value.indexOf(option.code) > -1}
            size="small"
            disableRipple
          />
          <ListItemText primary={option.name} />
        </MenuItem>
      ))}
      {filteredOptions.length === 0 && (
        <MenuItem disabled value="">
          <ListItemText primary={noResultsText} />
        </MenuItem>
      )}
    </Select>
  );

  if (!label) {
    return <Box sx={sx}>{selectControl}</Box>;
  }

  return (
    <FormControl size={size} sx={sx}>
      {/* `shrink` is forced: with `displayEmpty` the closed control ALWAYS
          shows text ("Wszystkie kraje" when nothing is picked), but MUI
          only auto-shrinks the label for a non-empty value -- without this
          the un-shrunk label rendered straight across the summary text. */}
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>
      {selectControl}
    </FormControl>
  );
}
